import { type ApiSchemas, call } from '@rental/contracts/client'
import { format, formatDateTime } from '@rental/i18n'
import { Checkbox, Field, Notice } from '@rental/ui'
import { type RefObject, useState } from 'react'
import { api } from '../api'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { OWNER_CODE, OwnerCodeField } from '../components/OwnerCodeField'
import { ProblemNotice, StaffInput } from '../components/ui'
import { useSend } from '../fines/send'
import { useLocale } from '../i18n'
import { type Person, type PersonDevice, preTicked, signOutChoices } from './people'

/** The server's reason: three characters or more, trimmed. */
const REASON_MIN = 3

type DialogProps<T> = {
  dialogRef: RefObject<HTMLDialogElement | null>
  person: Person
  onDone: (answer: T) => void
}

/**
 * Access given again (a password, a key) is approved with the Owner's code,
 * which the server spends in the same transaction: wrong, used or right, a
 * new one is typed after any answer. The question stays open on a refusal.
 */
function useCode() {
  const [code, setCode] = useState('')
  return { code, setCode, ready: OWNER_CODE.test(code), clear: () => setCode('') }
}

/** A new one-time password for someone else: the old one stops at once (S9, Q-51). */
export function PasswordDialog({
  dialogRef,
  person,
  onDone,
}: DialogProps<ApiSchemas['PersonPasswordReset']>) {
  const { messages: m } = useLocale()
  const copy = m.person
  const { send, busy, problem, clear } = useSend()
  const code = useCode()

  async function confirm() {
    if (!code.ready) return
    const answer = await send(() =>
      call(
        api.POST('/v1/staff/people/{userId}/password', {
          params: { path: { userId: person.id } },
          body: { code: code.code },
        }),
      ),
    )
    code.clear()
    if (!answer) return
    dialogRef.current?.close()
    onDone(answer)
  }

  return (
    <ConfirmDialog
      dialogRef={dialogRef}
      title={format(copy.passwordTitle, { name: person.displayName })}
      confirmLabel={copy.passwordConfirm}
      cancelLabel={copy.keep}
      busy={busy}
      ready={code.ready}
      reasons={[copy.why.code]}
      onConfirm={() => void confirm()}
      onClose={() => {
        clear()
        code.clear()
      }}
    >
      <p>{copy.passwordBody}</p>
      <OwnerCodeField label={copy.code} value={code.code} onChange={code.setCode} />
      <ProblemNotice problem={problem} />
    </ConfirmDialog>
  )
}

/** A new authenticator key for someone who signs in with a code: a lost or changed phone. */
export function KeyDialog({
  dialogRef,
  person,
  onDone,
}: DialogProps<ApiSchemas['PersonAuthenticatorReset']>) {
  const { messages: m } = useLocale()
  const copy = m.person
  const { send, busy, problem, clear } = useSend()
  const code = useCode()

  async function confirm() {
    if (!code.ready) return
    const answer = await send(() =>
      call(
        api.POST('/v1/staff/people/{userId}/authenticator', {
          params: { path: { userId: person.id } },
          body: { code: code.code },
        }),
      ),
    )
    code.clear()
    if (!answer) return
    dialogRef.current?.close()
    onDone(answer)
  }

  return (
    <ConfirmDialog
      dialogRef={dialogRef}
      title={format(copy.keyTitle, { name: person.displayName })}
      confirmLabel={copy.keyConfirm}
      cancelLabel={copy.keep}
      busy={busy}
      ready={code.ready}
      reasons={[copy.why.code]}
      onConfirm={() => void confirm()}
      onClose={() => {
        clear()
        code.clear()
      }}
    >
      <p>{copy.keyBody}</p>
      <OwnerCodeField label={copy.code} value={code.code} onChange={code.setCode} />
      <ProblemNotice problem={problem} />
    </ConfirmDialog>
  )
}

/** The reason, kept in the log with the change: three characters or more. */
function ReasonField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { messages: m } = useLocale()
  return (
    <Field label={m.person.reason} hint={m.person.reasonHint} required>
      {(control) => (
        <StaffInput
          {...control}
          maxLength={500}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  )
}

/**
 * Taking access away (S9, D11): at once, without a code, with a reason. Their
 * sessions end now; the devices ticked are signed out with them. Ticked from
 * the start are those nobody else used (a runner's own phone); a shared iPad
 * is offered, not ticked; the device asking is not offered.
 */
export function DeactivateDialog({
  dialogRef,
  person,
  devices,
  onDone,
}: DialogProps<ApiSchemas['StaffPerson']> & { devices: readonly PersonDevice[] }) {
  const { locale, messages: m } = useLocale()
  const copy = m.person
  const { send, busy, problem, clear } = useSend()
  const [reason, setReason] = useState('')
  // What the Owner changed from the ticks offered: the devices as last read decide the rest,
  // so a page read again while the question waits never leaves a stale tick behind.
  const [changed, setChanged] = useState<ReadonlyMap<string, boolean>>(new Map())
  const choices = signOutChoices(devices)
  const offered = new Set(preTicked(devices))
  const ticked = (id: string) => changed.get(id) ?? offered.has(id)
  const ready = reason.trim().length >= REASON_MIN

  async function confirm() {
    if (!ready) return
    const answer = await send(() =>
      call(
        api.POST('/v1/staff/people/{userId}/deactivate', {
          params: { path: { userId: person.id } },
          body: {
            version: person.version,
            reason: reason.trim(),
            // Only those still offered: a device signed out meanwhile is not sent.
            revokeDeviceIds: choices.filter((d) => ticked(d.id)).map((d) => d.id),
          },
        }),
      ),
    )
    if (!answer) return
    setReason('')
    setChanged(new Map())
    dialogRef.current?.close()
    onDone(answer)
  }

  return (
    <ConfirmDialog
      dialogRef={dialogRef}
      title={format(copy.deactivateTitle, { name: person.displayName })}
      confirmLabel={copy.deactivateConfirm}
      cancelLabel={copy.keep}
      busy={busy}
      ready={ready}
      reasons={[format(copy.why.reason, { min: REASON_MIN })]}
      onConfirm={() => void confirm()}
      onClose={clear}
    >
      <p>{copy.deactivateBody}</p>
      <ReasonField value={reason} onChange={setReason} />
      {choices.length > 0 ? (
        <fieldset className="flex flex-col gap-1">
          <legend className="text-sm font-medium text-text">{copy.signOut}</legend>
          <p className="text-xs text-text-muted">{copy.signOutHint}</p>
          {choices.map((device) => (
            <Checkbox
              key={device.id}
              checked={ticked(device.id)}
              onChange={(event) => {
                const checked = event.target.checked
                setChanged((current) => new Map(current).set(device.id, checked))
              }}
              label={
                <span className="flex flex-col">
                  <span>{device.name}</span>
                  <span className="text-sm text-text-muted">
                    {device.onlyThisPerson
                      ? copy.deviceOnly
                      : device.lastUsedAt
                        ? format(copy.deviceLastUsed, {
                            date: formatDateTime(locale, device.lastUsedAt),
                          })
                        : copy.deviceNeverUnlocked}
                  </span>
                </span>
              }
            />
          ))}
        </fieldset>
      ) : null}
      <ProblemNotice problem={problem} />
    </ConfirmDialog>
  )
}

/** Access back as it was, with a reason and the Owner's code. */
export function ReactivateDialog({
  dialogRef,
  person,
  expired,
  onDone,
}: DialogProps<ApiSchemas['StaffPerson']> & {
  /** Their one-time password has run out: they need a new one after. */
  expired: boolean
}) {
  const { messages: m } = useLocale()
  const copy = m.person
  const { send, busy, problem, clear } = useSend()
  const [reason, setReason] = useState('')
  const code = useCode()
  const reasonReady = reason.trim().length >= REASON_MIN
  const reasons = [
    ...(reasonReady ? [] : [format(copy.why.reason, { min: REASON_MIN })]),
    ...(code.ready ? [] : [copy.why.code]),
  ]

  async function confirm() {
    if (reasons.length > 0) return
    const answer = await send(() =>
      call(
        api.POST('/v1/staff/people/{userId}/reactivate', {
          params: { path: { userId: person.id } },
          body: { version: person.version, reason: reason.trim(), code: code.code },
        }),
      ),
    )
    code.clear()
    if (!answer) return
    dialogRef.current?.close()
    setReason('')
    onDone(answer)
  }

  return (
    <ConfirmDialog
      dialogRef={dialogRef}
      title={format(copy.reactivateTitle, { name: person.displayName })}
      confirmLabel={copy.reactivateConfirm}
      cancelLabel={copy.keep}
      busy={busy}
      ready={reasons.length === 0}
      reasons={reasons}
      onConfirm={() => void confirm()}
      onClose={() => {
        clear()
        code.clear()
      }}
    >
      <p>{copy.reactivateBody}</p>
      {expired ? (
        <Notice tone="warning">
          <p>{copy.reactivateExpired}</p>
        </Notice>
      ) : null}
      <ReasonField value={reason} onChange={setReason} />
      <OwnerCodeField label={copy.code} value={code.code} onChange={code.setCode} />
      <ProblemNotice problem={problem} />
    </ConfirmDialog>
  )
}
